// src/pages/customer/Products.jsx
import React, { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
import { getProducts, getCategories } from "../../services/firestoreService";
import ProductCard from "../../components/customer/ProductCard";
import Spinner from "../../components/common/Spinner";
import {
  FiSearch, FiX, FiSliders, FiChevronDown, FiPackage,
} from "react-icons/fi";
import leafBg from "../../assests/background.png";

const SORT_OPTIONS = [
  { value: "newest",     label: "Newest first" },
  { value: "price-asc",  label: "Price: Low → High" },
  { value: "price-desc", label: "Price: High → Low" },
  { value: "name",       label: "Name A–Z" },
];

export default function Products() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products,    setProducts]   = useState([]);
  const [categories,  setCategories] = useState([]);
  const [loading,     setLoading]    = useState(true);
  const [search,      setSearch]     = useState(searchParams.get("search") || "");
  const [category,    setCategory]   = useState(searchParams.get("category") || "");
  const [sort,        setSort]       = useState("newest");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [p, c] = await Promise.all([getProducts(), getCategories()]);
      setProducts(p);
      setCategories(c);
      setLoading(false);
    };
    load();
  }, []);

  useEffect(() => {
    setSearch(searchParams.get("search") || "");
    setCategory(searchParams.get("category") || "");
  }, [searchParams]);

  const filtered = useMemo(() => {
    let list = [...products];
    if (search)   list = list.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()) || (p.description || "").toLowerCase().includes(search.toLowerCase()));
    if (category) list = list.filter((p) => p.categoryId === category);
    if (sort === "price-asc")  list.sort((a, b) => a.price - b.price);
    if (sort === "price-desc") list.sort((a, b) => b.price - a.price);
    if (sort === "name")       list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [products, search, category, sort]);

  const handleSearch = (val) => {
    setSearch(val);
    const params = new URLSearchParams(searchParams);
    if (val) params.set("search", val); else params.delete("search");
    setSearchParams(params);
  };

  const handleCategory = (val) => {
    setCategory(val);
    setSidebarOpen(false);
    const params = new URLSearchParams(searchParams);
    if (val) params.set("category", val); else params.delete("category");
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearch(""); setCategory(""); setSearchParams({});
  };

  const activeFilters = (search ? 1 : 0) + (category ? 1 : 0);
  const activeCatName = categories.find((c) => c.id === category)?.name;

  return (
    <div className="min-h-screen bg-gray-50">

      <Helmet>
        <title>{activeCatName ? `${activeCatName} | Tripura Agro` : "All Products | Tripura Agro"}</title>
        <meta
          name="description"
          content="Tripura Agro Industries was established in 2022 with the goal of leveraging supply and demand between farmers and Mandis at the national level and the final customer, who may be an individual or an institution."
        />
      </Helmet>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-leaf-600 to-primary-700 py-10 sm:py-16 px-4 text-white">
        <div className="pointer-events-none absolute -top-24 -left-24 w-96 h-96 rounded-full border border-white/10" />
        <div className="pointer-events-none absolute -bottom-16 -right-16 w-72 h-72 rounded-full border border-white/10" />
        <div className="max-w-7xl mx-auto relative">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase bg-white/20 px-4 py-1.5 rounded-full mb-3 sm:mb-4">
            {activeCatName || "All Products"}
          </span>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold mb-2 sm:mb-3 leading-tight" style={{ fontFamily: "var(--custom-body-font-family)" }}>
            {activeCatName || "Our Products"}
          </h1>
          <p className="text-white/80 text-sm sm:text-base max-w-xl">
            Premium quality agricultural products sourced directly from trusted farms across India.
          </p>
          {/* Search bar */}
          <div className="mt-6 sm:mt-8 max-w-xl relative">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60 w-5 h-5" />
            <input
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search products..."
              className="w-full bg-white/15 backdrop-blur-sm border border-white/30 text-white placeholder-white/50 rounded-2xl pl-12 pr-12 py-3 sm:py-3.5 text-sm focus:outline-none focus:bg-white/25 focus:border-white/50 transition-all"
            />
            {search && (
              <button onClick={() => handleSearch("")} className="absolute right-4 top-1/2 -translate-y-1/2">
                <FiX className="w-4 h-4 text-white/70 hover:text-white" />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* ── Body ── */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-10 relative overflow-hidden">
        {/* Decorative leaf branch background — sits behind the product grid */}
        <img
          src={leafBg}
          alt=""
          aria-hidden="true"
          className="absolute bottom-0 -left-16 w-[420px] sm:w-[560px] pointer-events-none select-none"
          style={{ opacity: 0.12, zIndex: 0 }}
        />

        <div className="relative" style={{ zIndex: 1 }}>

        {/* Toolbar */}
        <div className="flex items-center justify-between gap-3 mb-5 sm:mb-6 flex-wrap">
          <div className="flex items-center gap-2">
            <p className="text-sm text-gray-500">
              <span className="font-semibold text-gray-800">{filtered.length}</span> products found
            </p>
            {activeFilters > 0 && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 px-2.5 py-1 rounded-full transition-colors"
              >
                <FiX className="w-3 h-3" /> Clear
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {/* Filter toggle — mobile only */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-xl border text-xs sm:text-sm font-medium transition-colors lg:hidden
                ${sidebarOpen ? "bg-primary-50 border-primary-300 text-primary-700" : "bg-white border-gray-200 text-gray-700"}`}
            >
              <FiSliders className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              Filters
              {activeFilters > 0 && (
                <span className="w-4 h-4 sm:w-5 sm:h-5 bg-primary-600 text-white rounded-full text-[10px] sm:text-xs flex items-center justify-center">
                  {activeFilters}
                </span>
              )}
            </button>

            {/* Sort */}
            <div className="relative">
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="appearance-none bg-white border border-gray-200 text-gray-700 text-xs sm:text-sm rounded-xl pl-3 sm:pl-4 pr-7 sm:pr-9 py-2 font-medium focus:outline-none focus:border-primary-400 cursor-pointer"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <FiChevronDown className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* ── Mobile filter drawer (slides down, ABOVE the grid) ── */}
        {sidebarOpen && (
          <div className="lg:hidden mb-4 bg-white rounded-2xl border border-gray-200 shadow-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 text-sm">Categories</h3>
              <div className="flex items-center gap-3">
                {category && (
                  <button onClick={() => handleCategory("")} className="text-xs text-primary-600 hover:underline">
                    Clear
                  </button>
                )}
                <button onClick={() => setSidebarOpen(false)} className="text-gray-400 hover:text-gray-600">
                  <FiX className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Horizontal scrollable pills on mobile */}
            <div className="flex gap-2 overflow-x-auto pb-1">
              <button
                onClick={() => handleCategory("")}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors
                  ${!category ? "bg-primary-600 text-white border-primary-600" : "bg-white text-gray-600 border-gray-200 hover:border-primary-400"}`}
              >
                All ({products.length})
              </button>
              {categories.map((c) => {
                const count = products.filter((p) => p.categoryId === c.id).length;
                return (
                  <button
                    key={c.id}
                    onClick={() => handleCategory(c.id)}
                    className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors
                      ${category === c.id ? "bg-primary-600 text-white border-primary-600" : "bg-white text-gray-600 border-gray-200 hover:border-primary-400"}`}
                  >
                    {c.name} ({count})
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Main layout: sidebar (desktop) + grid ── */}
        <div className="flex gap-6 lg:gap-8">

          {/* Sidebar — desktop only, never renders on mobile */}
          <aside className="hidden lg:block w-56 flex-shrink-0">
            <div className="bg-white rounded-2xl border border-gray-100 p-5 sticky top-24">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 text-sm">Categories</h3>
                {category && (
                  <button onClick={() => handleCategory("")} className="text-xs text-primary-600 hover:underline">
                    Clear
                  </button>
                )}
              </div>
              <div className="space-y-1">
                <button
                  onClick={() => handleCategory("")}
                  className={`w-full text-left px-3 py-2.5 rounded-xl text-sm transition-colors flex items-center justify-between
                    ${!category ? "bg-primary-600 text-white font-medium" : "text-gray-600 hover:bg-gray-50"}`}
                >
                  <span>All Products</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-md ${!category ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"}`}>
                    {products.length}
                  </span>
                </button>
                {categories.map((c) => {
                  const count = products.filter((p) => p.categoryId === c.id).length;
                  return (
                    <button
                      key={c.id}
                      onClick={() => handleCategory(c.id)}
                      className={`w-full text-left px-3 py-2.5 rounded-xl text-sm transition-colors flex items-center justify-between gap-2
                        ${category === c.id ? "bg-primary-600 text-white font-medium" : "text-gray-600 hover:bg-gray-50"}`}
                    >
                      <span className="truncate">{c.name}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-md flex-shrink-0 ${category === c.id ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"}`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="mt-5 pt-5 border-t border-gray-100">
                <p className="text-xs text-gray-400 text-center">Showing all available products</p>
              </div>
            </div>
          </aside>

          {/* ── Product grid — always full width on mobile ── */}
          <div className="flex-1 min-w-0 w-full">
            {loading ? (
              <div className="flex items-center justify-center py-32">
                <Spinner center />
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-3xl border border-gray-100">
                <div className="w-20 h-20 bg-gray-50 rounded-3xl flex items-center justify-center mx-auto mb-5">
                  <FiPackage className="w-9 h-9 text-gray-300" />
                </div>
                <h3 className="text-xl font-bold text-gray-700 mb-2">No products found</h3>
                <p className="text-gray-400 text-sm mb-6">Try adjusting your search or filters</p>
                <button onClick={clearFilters} className="btn-secondary">Clear filters</button>
              </div>
            ) : (
              /* 2 cols on mobile, 3 on md, 4 on xl */
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-5">
                {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
            )}
          </div>

        </div>
        </div>
      </div>
    </div>
  );
}